import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { commons } from "../mock";
import Reveal from "../components/Reveal";

/* Large editorial feature piece — big image + eyebrow, title, sub-line, multi-paragraph explainer */
function FeatureCard({ theme, reverse }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <Reveal className={reverse ? "lg:order-2" : ""}>
        <div className="relative overflow-hidden rounded-md group">
          <img src={theme.img} alt={theme.title} className="w-full h-[420px] md:h-[580px] object-cover bw transition-transform duration-[1400ms] group-hover:scale-105" />
          <div className="absolute inset-0 ring-1 ring-inset ring-gold/25 rounded-md" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908]/60 to-transparent" />
        </div>
      </Reveal>
      <Reveal delay={120} className={reverse ? "lg:order-1" : ""}>
        <div>
          <p className="eyebrow mb-4">{theme.category}</p>
          <h2 className="font-display normal-case font-bold text-white text-4xl md:text-6xl leading-[1.05]">{theme.title}</h2>
          {theme.subline && (
            <p className="font-serif-accent italic text-xl md:text-2xl text-gold mt-4">{theme.subline}</p>
          )}
          <div className="space-y-4 mt-7">
            {theme.paragraphs.map((p, i) => (
              <p key={i} className="font-body text-white/65 text-lg leading-relaxed font-light">{p}</p>
            ))}
          </div>
          <Link to="/contact" className="gold-lift inline-flex items-center gap-2 mt-8 border border-gold/60 text-gold font-display uppercase font-bold tracking-[0.16em] text-xs px-7 py-3.5 rounded-sm hover:gold-gradient-bg hover:text-[#0a0908] transition-colors">
            Get in touch <ArrowRight size={14} />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}

/* Standard theme card — image + eyebrow, title, sub-line, short explainer, buttons */
function ThemeCard({ theme, reverse }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <Reveal className={reverse ? "lg:order-2" : ""}>
        <div className="relative overflow-hidden rounded-md group">
          <img src={theme.img} alt={theme.title} className="w-full h-[300px] md:h-[420px] object-cover bw transition-transform duration-[1400ms] group-hover:scale-105" />
          <div className="absolute inset-0 ring-1 ring-inset ring-gold/25 rounded-md" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908]/50 to-transparent" />
        </div>
      </Reveal>
      <Reveal delay={120} className={reverse ? "lg:order-1" : ""}>
        <div>
          <p className="eyebrow mb-4">{theme.category}</p>
          <h2 className="font-display normal-case font-bold text-white text-3xl md:text-5xl leading-tight">{theme.title}</h2>
          {theme.subline && (
            <p className="font-serif-accent italic text-lg md:text-xl text-gold mt-3">{theme.subline}</p>
          )}
          <p className="font-body text-white/60 mt-5 text-lg leading-relaxed">{theme.explainer}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/contact" className="gold-lift inline-flex items-center gap-2 border border-gold/60 text-gold font-display uppercase font-bold tracking-[0.16em] text-xs px-7 py-3.5 rounded-sm hover:gold-gradient-bg hover:text-[#0a0908] transition-colors">Get in touch <ArrowRight size={14} /></Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}

export default function Commons() {
  const { hero, intro, themes, closing } = commons;
  const features = themes.filter((t) => t.feature);
  const standard = themes.filter((t) => !t.feature);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-center bg-cover bw" style={{ backgroundImage: `url(${hero.bg})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908]/85 via-[#0a0908]/78 to-[#0a0908]" />
        <div className="grain" />
        <div className="container-x relative z-10 text-center pt-24">
          <Reveal><p className="eyebrow mb-6">{hero.eyebrow}</p></Reveal>
          <Reveal delay={80}><h1 className="font-display uppercase font-bold text-white text-5xl md:text-7xl leading-none">{hero.title}</h1></Reveal>
          <Reveal delay={160}><p className="font-serif-accent italic text-2xl md:text-4xl lg:text-5xl text-white/85 mt-8 leading-snug">{hero.subtitle}</p></Reveal>
        </div>
      </section>
      <div className="gold-rule" />

      {/* INTRO */}
      <section className="pt-16 pb-4 bg-[#0a0908]">
        <div className="container-x max-w-3xl text-center">
          <Reveal><p className="font-body text-white/70 text-lg md:text-xl leading-relaxed">{intro}</p></Reveal>
        </div>
      </section>

      {/* FEATURE PIECES */}
      <section className="section-pad bg-[#0a0908]">
        <div className="container-x space-y-24 md:space-y-32">
          {features.map((t, i) => (
            <React.Fragment key={t.title}>
              <FeatureCard theme={t} reverse={i % 2 === 1} />
              {i < features.length - 1 && <div className="gold-rule max-w-xs mx-auto" />}
            </React.Fragment>
          ))}
        </div>
      </section>
      <div className="gold-rule" />

      {/* STANDARD PIECES */}
      <section className="section-pad bg-[#0b0a08]">
        <div className="container-x space-y-20 md:space-y-28">
          {standard.map((t, i) => (
            <ThemeCard key={t.title} theme={t} reverse={i % 2 === 1} />
          ))}
        </div>
      </section>

      {/* CLOSING CTA (purple) */}
      <section className="relative section-pad overflow-hidden bg-melano-purple">
        <div className="absolute inset-0 bg-center bg-cover bw opacity-25" style={{ backgroundImage: `url(${closing.bg})` }} />
        <div className="absolute inset-0 bg-[hsl(var(--melano-purple))]/78" />
        <div className="grain" />
        <div className="container-x text-center relative z-10 py-10">
          <Reveal><h2 className="font-display uppercase font-bold text-4xl md:text-6xl text-white">{closing.heading}</h2></Reveal>
          <Reveal delay={110}><p className="font-serif-accent italic text-2xl md:text-3xl text-gold mt-5">{closing.lead}</p></Reveal>
          <Reveal delay={150}><p className="text-white/80 mt-5 max-w-2xl mx-auto">{closing.body}</p></Reveal>
          <Reveal delay={200}>
            <Link to={closing.cta.to} className="gold-lift mt-10 inline-flex items-center gap-2 gold-gradient-bg text-[#0a0908] font-display uppercase font-bold tracking-[0.18em] text-sm px-9 py-4 rounded-sm hover:brightness-110 transition">
              {closing.cta.label} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
